/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { ChevronDown, ChevronUp } from 'lucide-react';
import { PropsWithChildren, useContext, useState } from 'react';

import { formatError } from '../atoms/Error';

import {
    AIAssertionStep,
    AppErrorMessageBug,
    AppException,
    ApplicationErrorBug,
    AppNetworkRequest,
    Bug,
    Step,
} from '@/commons/data';
import { StepContext } from '@/pages/projects/RunViewer';
import { useUserContext } from '@/pages/Root';

type BugCardProps = {
    title: string;
    subtitle?: string;
    defaultOpen: boolean;
};

const BugCard = ({ title, subtitle, defaultOpen, children }: PropsWithChildren<BugCardProps>) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="flex flex-col">
            <h4
                className="flex flex-row w-full justify-between  items-center pr-3
                text text-sm font-medium text-primary mb-0 "
            >
                {title}

                {open ? (
                    <ChevronUp onClick={() => setOpen(false)} size={14} className="inline-block" />
                ) : (
                    <ChevronDown onClick={() => setOpen(true)} size={14} className="inline-block" />
                )}
            </h4>
            {open && subtitle && <div className="mb-2 text-xs text-muted-foreground">{subtitle}</div>}
            {open && <div className="flex w-full">{children}</div>}
        </div>
    );
};

type ActionOutputProps = {
    stepResultsFromBack: Step[];
    stepResultsFromWebSocket: Step[];
    error: Error | null | undefined;
};

const ActionBugs = ({ stepResultsFromBack, stepResultsFromWebSocket, error }: ActionOutputProps) => {
    const { runStatus, highlightedAction, highlightedStepRank } = useContext(StepContext);
    const { canOperate } = useUserContext().user;

    const steps = runStatus === 'running' || runStatus === 'queued' ? stepResultsFromWebSocket : stepResultsFromBack;
    const step = steps.find((s) => s.rank === highlightedStepRank);
    const getActionToDisplay = () => {
        if (step) {
            if (step.kind === 'aiAgent' || step.kind === 'recordedActions') {
                return highlightedAction && highlightedAction ? step.actions[highlightedAction] : undefined;
            }
            return step.actions[0];
        }
    };

    if (error) {
        return (
            <div className="flex flex-col gap-1">
                <h4 className="font-medium text-gray-800">Error: </h4>
                <p className="text-muted-foreground text-xs leading-tight">{formatError(error)}</p>
            </div>
        );
    }

    const actionToDisplay = getActionToDisplay();
    const bugs = actionToDisplay?.bugs;

    const groupBugs = (
        bugsToGroup: Bug[],
    ): {
        errorMessages: AppErrorMessageBug[];
        exceptions: AppException[];
        networkRequests: AppNetworkRequest[];
    } => {
        const res: {
            errorMessages: AppErrorMessageBug[];
            exceptions: AppException[];
            networkRequests: AppNetworkRequest[];
        } = { errorMessages: [], exceptions: [], networkRequests: [] };

        for (const bug of bugsToGroup) {
            switch (bug.kind) {
                case 'errorMessage':
                    res.errorMessages.push(bug as AppErrorMessageBug);
                    break;
                case 'applicationError':
                    const appError = bug as ApplicationErrorBug;
                    res.exceptions.push(...appError.exceptions);
                    res.networkRequests.push(...appError.brokenRequests);
                    break;
            }
        }

        return res;
    };

    if (step?.kind === 'aiAssertion' && (step as AIAssertionStep).actions?.length > 0) {
        return (
            <div className="flex flex-col gap-2 overflow-y-scroll sm:overflow-x-clip">
                {step?.kind === 'aiAssertion' && (step as AIAssertionStep).actions?.length > 0 && (
                    <BugCard
                        title={(step as AIAssertionStep).result === 'PASS' ? 'Assertion passed' : 'Assertion failed'}
                        defaultOpen={true}
                    >
                        <div className="ml-3 text-xs">{(step as AIAssertionStep).actions[0].observations}</div>
                    </BugCard>
                )}
            </div>
        );
    }
    if (actionToDisplay?.result === 'CRASH' && canOperate) {
        return (
            <div className="flex flex-col gap-2 overflow-y-scroll sm:overflow-x-clip">
                <BugCard title={'(Admin only) Crash report'} defaultOpen={true}>
                    <div className="ml-3 text-xs"> {actionToDisplay.error ?? 'No reported error reasons'}</div>
                </BugCard>
            </div>
        );
    }
    if (step?.kind === 'simpleAssertion' && step.result === 'FAIL')
        return <div className="text-sm">Assertion failed</div>;

    if (bugs === undefined || bugs.length === 0) {
        return <div className="text-sm">No issue detected for this step</div>;
    }

    const groupedBugs = groupBugs(bugs);
    return (
        <div className="flex flex-col gap-2 overflow-y-scroll sm:overflow-x-clip">
            {groupedBugs.errorMessages.length > 0 && (
                <BugCard title={'Error message detected'} defaultOpen={true}>
                    {groupedBugs.errorMessages.map((m, j) => (
                        <div className="ml-3 text-xs" key={`error-message-${j}`}>
                            "{m.message}"
                        </div>
                    ))}
                </BugCard>
            )}
            {groupedBugs.networkRequests.length > 0 && (
                <BugCard
                    title={'Failed network requests'}
                    subtitle={'Heal filters requests that likely caused this action to fail'}
                    defaultOpen={true}
                >
                    <table className=" w-full ml-3 text-xs mt-0 border-collapse overflow-clip text-ellipsis">
                        {groupedBugs.networkRequests.map((r, i) => (
                            <tr key={i} className="">
                                <td className="border px-6">{r.method}</td>
                                <td className="border px-6">{r.statusCode}</td>
                                <td className="border px-6">{r.url}</td>
                            </tr>
                        ))}
                    </table>
                </BugCard>
            )}
            {groupedBugs.exceptions.length > 0 && (
                <BugCard
                    title={'Exceptions'}
                    defaultOpen={true}
                    subtitle={'Heal filters exceptions that likely caused this action to fail'}
                >
                    <table className="ml-3 text-xs">
                        {groupedBugs.exceptions.map((e, i) => (
                            <tr key={i}>
                                <td>
                                    {e.message}
                                    {e.caught ? ' (caught)' : ' (uncaught)'}
                                </td>
                            </tr>
                        ))}
                    </table>
                </BugCard>
            )}
        </div>
    );
};

export default ActionBugs;
